import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { WEB_CONFIG, WebAppEnvironment } from '@buspro/web/core';
import { Observable } from 'rxjs';

/**
 * Mirrors the backend GeofenceAdjustment endpoints. Hand-rolled HttpClient calls, following
 * [StandardPlaceImpactService] — the generated client predates these routes, and the next
 * `pnpm generate-api` against a backend that serves them can replace this with the typed client.
 */
export interface GeofenceConflictPair {
  a: string;
  b: string;
  metresApart: number;
  overlapMetres: number;
  aRadius: number;
  bRadius: number;
  routeNames: string[];
}

export interface PlaceConflict {
  standardPlaceId: string;
  placeName: string;
  placeType: string;
  currentRadius: number;
  /** null when shrinking this place alone cannot clear every conflict. */
  recommendedRadius: number | null;
  blockedByPartner: string | null;
  conflicts: GeofenceConflictPair[];
  routesAffected: number;
  /** This place is the wider zone in at least one conflict — fixing it likely clears the rest. */
  isLikelyCulprit: boolean;
}

export interface AdjustmentRequest {
  id: string;
  standardPlaceId: string;
  placeName: string;
  placeType: string;
  currentRadius: number;
  recommendedRadius: number | null;
  blockedByPartner: string | null;
  conflicts: GeofenceConflictPair[];
  source: 'TENANT_BLOCKED' | 'ADMIN_SWEEP';
  requesterName: string | null;
  routeName: string | null;
  status: 'PENDING' | 'APPLIED' | 'REJECTED' | 'SUPERSEDED';
  appliedRadius: number | null;
  appliedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
}

export interface AdjustmentSummary {
  pendingRequests: number;
  placesInConflict: number;
  unreportedPlaces: number;
  clearanceMetres: number;
}

@Injectable({ providedIn: 'root' })
export class GeofenceAdjustmentService {
  private http = inject(HttpClient);
  private environment = inject<WebAppEnvironment>(WEB_CONFIG);

  private get base(): string {
    return `${this.environment.api}/geofence-adjustments`;
  }

  conflicts(): Observable<PlaceConflict[]> {
    return this.http.get<PlaceConflict[]>(`${this.base}/conflicts`);
  }

  requests(status?: string): Observable<AdjustmentRequest[]> {
    return this.http.get<AdjustmentRequest[]>(this.base, { params: status ? { status } : {} });
  }

  summary(): Observable<AdjustmentSummary> {
    return this.http.get<AdjustmentSummary>(`${this.base}/summary`);
  }

  raise(standardPlaceId: string, routeName?: string): Observable<AdjustmentRequest> {
    return this.http.post<AdjustmentRequest>(`${this.base}/request`, { standardPlaceId, routeName });
  }

  apply(id: string): Observable<AdjustmentRequest> {
    return this.http.post<AdjustmentRequest>(`${this.base}/${id}/apply`, {});
  }

  reject(id: string, reason: string): Observable<AdjustmentRequest> {
    return this.http.post<AdjustmentRequest>(`${this.base}/${id}/reject`, { reason });
  }
}
